import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  listGames,
  createGame,
  updateGame,
  deleteGame,
  fetchCategories as fetchCategoriesController
} from '../controllers/gamesController';
import { listUsers, createUser } from '../controllers/usersController';
import { createTransaction, updateTransaction } from '../controllers/transactionsController';
import { createTransactionPayload } from '../entities/transaction';
import PosPrinter from '../components/PosPrinter';
import TopPlayersLeaderboard from '../components/TopPlayersLeaderboard';

const initialFormValues = {
  name: '',
  description: '',
  category: '',
  category_custom: '',
  duration: '',
  charges_default: '',
  charges_30min: '',
  charges_1hr: '',
  charges_2hr: '',
  charges_5overs: 0,
  charges_10overs: 0,
  charges_20overs: 0,
  units: 'minutes',
  status: 'active',
  istrending: false,
  challenge_type: '',
  challenge_type_custom: '',
  challenge_start_date: '',
  challenge_end_date: '',
  challenge_total_participants: '',
  challenge_winner_reward: '',
  challenge_price_per_participant: '',
  challenge_participants: ''
};

const placeholderImage =
  'https://images.unsplash.com/photo-1511512578047-dfb367046420?auto=format&fit=crop&w=900&q=80';

const toDateTimeLocal = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (num) => String(num).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const toIsoString = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return '₹0.00';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

function GameMediaCarousel({ media, placeholder, title, game, canViewBadges, showCategoryBadge }) {
  const assets = useMemo(
    () => (Array.isArray(media) ? media.filter((item) => item && item.url) : []),
    [media]
  );
  const [index, setIndex] = useState(0);
  const [resolvedAssets, setResolvedAssets] = useState(assets);

  // Simple in-memory cache of fetched media -> blob URLs to avoid refetching
  // across carousel renders. Kept at module lifetime.
  /* eslint-disable no-use-before-define */
  if (typeof window !== 'undefined' && !window.__mediaBlobUrlCache) {
    window.__mediaBlobUrlCache = new Map();
    window.__mediaCreatedBlobUrls = new Set();
    // revoke created blob urls on unload
    window.addEventListener('beforeunload', () => {
      window.__mediaCreatedBlobUrls.forEach((u) => URL.revokeObjectURL(u));
    });
  }
  const mediaBlobUrlCache = (typeof window !== 'undefined') ? window.__mediaBlobUrlCache : new Map();
  const mediaCreatedBlobUrls = (typeof window !== 'undefined') ? window.__mediaCreatedBlobUrls : new Set();
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // reset index when assets change
  useEffect(() => {
    setIndex(0);
    setResolvedAssets(assets);
  }, [assets.length]);

  // Resolve remote URLs to blob URLs (cache) to avoid reloading repeatedly
  useEffect(() => {
    let mounted = true;
    if (!assets || assets.length === 0) return undefined;
    const resolve = async () => {
      const promises = assets.map(async (a) => {
        if (!a) return null;
        // local File already handled elsewhere
        if (typeof File !== 'undefined' && a instanceof File) return a;
        // data/blob URLs left as-is
        if (a.url && (a.url.startsWith('blob:') || a.url.startsWith('data:'))) return a;
        // if cached, return cached blob url
        if (mediaBlobUrlCache.has(a.url)) {
          return { ...a, url: mediaBlobUrlCache.get(a.url) };
        }
        // try fetching and creating blob URL
        try {
          const res = await fetch(a.url, { cache: 'force-cache' });
          if (!res.ok) throw new Error('fetch failed');
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          mediaBlobUrlCache.set(a.url, blobUrl);
          mediaCreatedBlobUrls.add(blobUrl);
          return { ...a, url: blobUrl };
        } catch (e) {
          // fallback to original url on error
          return a;
        }
      });
      const results = await Promise.all(promises);
      if (mounted) setResolvedAssets(results.filter(Boolean));
    };
    resolve();
    return () => { mounted = false; };
  }, [assets.map((a) => a && a.url).join('|')]);

  // use resolvedAssets (if available) for playback/auto-advance
  const workingAssets = (resolvedAssets && resolvedAssets.length > 0) ? resolvedAssets : assets;
  useEffect(() => {
    if (workingAssets.length <= 1) return undefined;
    const currentAsset = workingAssets[index];
    if (currentAsset && currentAsset.type === 'video') return undefined;
    const interval = setInterval(() => setIndex((prev) => (prev + 1) % workingAssets.length), 5000);
    return () => clearInterval(interval);
  }, [workingAssets.length, index]);

  const current = workingAssets[index];
  const isVideo = current?.type === 'video';
  const mediaKey = current ? `${current.url}-${index}` : 'placeholder';

  const handleTouchStart = (e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  };

  const handleTouchEnd = (e) => {
    const t = e.changedTouches && e.changedTouches[0];
    if (!t || touchStartX.current == null) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;
    // ignore mostly vertical swipes
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) {
      // swipe left -> next
      setIndex((i) => (i + 1) % assets.length);
    } else {
      // swipe right -> prev
      setIndex((i) => (i - 1 + assets.length) % assets.length);
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <div className="game-card-media-wrapper" style={{ position: 'relative', height: '100%' }} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
      {isVideo ? (
        <video
          key={mediaKey}
          className="game-card-media"
          src={current.url}
          muted
          playsInline
          autoPlay
          onEnded={() => setIndex((prev) => (prev + 1) % assets.length)}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img
          key={mediaKey}
          className="game-card-media"
          src={current?.url || placeholder}
          alt={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      
      {/* Overlay Badges */}
      {canViewBadges && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '0',
          right: '0',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '0 8px',
          pointerEvents: 'none'
        }}>
          {/* Category Badge - Left Side */}
          {game.category && showCategoryBadge && (
            <span 
              className="badge category-badge"
              style={{
                background: 'rgba(0, 229, 255, 0.9)',
                color: '#0b1735',
                fontSize: '0.7rem',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
              }}
            >
              {game.category}
            </span>
          )}
          
          {/* Status Badge - Right Side */}
          {game.status && (
            <span 
              className="badge status-badge"
              style={{
                background: game.status === 'active' ? 'rgba(34, 197, 94, 0.9)' : 'rgba(239, 68, 68, 0.9)',
                color: '#ffffff',
                fontSize: '0.7rem',
                fontWeight: '600',
                padding: '4px 8px',
                borderRadius: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
              }}
            >
              {game.status}
            </span>
          )}
        </div>
      )}
      
      {assets.length > 1 && (
        <div className="media-indicators">
          {assets.map((item, itemIndex) => (
            <span
              key={`${item.url}-${itemIndex}`}
              className={`media-indicator${itemIndex === index ? ' active' : ''}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function GamesPage() {
  const location = useLocation();
  const isPublicRoute = location.pathname === '/games';
  
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAssets, setPreviewAssets] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [generatedObjectUrls, setGeneratedObjectUrls] = useState([]);
  
  // Payment popup state
  const [paymentPopupOpen, setPaymentPopupOpen] = useState(false);
  const [userPhone, setUserPhone] = useState('');
  const [userName, setUserName] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [searchingUser, setSearchingUser] = useState(false);
  const [userSearchError, setUserSearchError] = useState(null);
  
  // Virtual keyboard state
  const [virtualKeyboardOpen, setVirtualKeyboardOpen] = useState(false);
  const [activeInput, setActiveInput] = useState(null); // 'phone' or 'name'
  const [keyboardLayout, setKeyboardLayout] = useState('phone'); // 'phone' or 'text'
  
  // POS printer state
  const [posPrinterOpen, setPosPrinterOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [posAutoPrint, setPosAutoPrint] = useState(false);
  
  // Payment options popup state
  const [paymentOptionsOpen, setPaymentOptionsOpen] = useState(false);
  const [paymentTimer, setPaymentTimer] = useState(40);
  const [timerActive, setTimerActive] = useState(false);
  const [proceedHint, setProceedHint] = useState('');
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [lastReceipt, setLastReceipt] = useState(null);

  const openPreview = (media = [], startIndex = 0) => {
    if (!Array.isArray(media)) media = [media];
    const generated = [];
    const assets = media
      .map((m) => {
        if (!m) return null;
        // File (local selection)
        if (typeof File !== 'undefined' && m instanceof File) {
          const url = URL.createObjectURL(m);
          generated.push(url);
          const isVideo = typeof m.type === 'string' && m.type.startsWith('video');
          return { url, type: isVideo ? 'video' : 'image' };
        }
        // String URL
        if (typeof m === 'string') {
          const isVideo = !!m.match(/\.(mp4|webm|ogg)(\?|$)/i);
          return { url: m, type: isVideo ? 'video' : 'image' };
        }
        // Object with url/type
        if (m.url) {
          const isVideo = m.type === 'video' || !!m.url.match(/\.(mp4|webm|ogg)(\?|$)/i);
          return { url: m.url, type: isVideo ? 'video' : 'image' };
        }
        return null;
      })
      .filter(Boolean);

    if (assets.length === 0) return;
    if (generated.length > 0) setGeneratedObjectUrls((prev) => prev.concat(generated));
    setPreviewAssets(assets);
    setPreviewIndex(Math.min(Math.max(0, startIndex), assets.length - 1));
    setPreviewOpen(true);
  };

  const closePreview = () => {
    // revoke any object URLs we created for local files
    try {
      (generatedObjectUrls || []).forEach((u) => URL.revokeObjectURL(u));
    } catch (e) {
      // ignore
    }
    setGeneratedObjectUrls([]);
    setPreviewOpen(false);
    setPreviewAssets([]);
    setPreviewIndex(0);
  };

  const openMediaPreviewFromForm = (mediaArray, startIndex = 0) => {
    // reuse existing openPreview logic but accept mixture of File and url objects
    const assets = mediaArray.map((m) => {
      if (!m) return null;
      if (typeof File !== 'undefined' && m instanceof File) {
        const url = URL.createObjectURL(m);
        setGeneratedObjectUrls(prev => prev.concat(url));
        const isVideo = typeof m.type === 'string' && m.type.startsWith('video');
        return { url, type: isVideo ? 'video' : 'image' };
      }
      if (m.url) {
        const isVideo = m.type === 'video' || !!m.url.match(/\.(mp4|webm|ogg)(\?|$)/i);
        return { url: m.url, type: isVideo ? 'video' : 'image' };
      }
      if (typeof m === 'string') {
        const isVideo = !!m.match(/\.(mp4|webm|ogg)(\?|$)/i);
        return { url: m, type: isVideo ? 'video' : 'image' };
      }
      return null;
    }).filter(Boolean);
    if (assets.length === 0) return;
    setPreviewAssets(assets);
    setPreviewIndex(Math.min(Math.max(0, startIndex), assets.length - 1));
    setPreviewOpen(true);
  };

  const prevPreview = () => setPreviewIndex((i) => (i - 1 + previewAssets.length) % previewAssets.length);
  const nextPreview = () => setPreviewIndex((i) => (i + 1) % previewAssets.length);

  // keyboard navigation
  useEffect(() => {
    if (!previewOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        closePreview();
        return;
      }
      if (e.key === 'ArrowLeft') prevPreview();
      if (e.key === 'ArrowRight') nextPreview();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [previewOpen, previewAssets.length]);

  // Regular keyboard support for payment inputs
  useEffect(() => {
    if (!paymentPopupOpen) return undefined;
    
    const handleKeyDown = (e) => {
      // Don't interfere if user is typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Handle special keys
      if (e.key === 'Escape') {
        closePaymentPopup();
        return;
      }
      
      // Handle backspace
      if (e.key === 'Backspace') {
        e.preventDefault();
        handleVirtualKeyPress('backspace');
        return;
      }
      
      // Handle numbers for phone input
      if (activeInput === 'phone' && /^[0-9]$/.test(e.key)) {
        e.preventDefault();
        if (userPhone.length < 10) {
          handleVirtualKeyPress(e.key);
        }
        return;
      }
      
      // Handle letters and space for name input
      if (activeInput === 'name' && /^[a-zA-Z\s]$/.test(e.key)) {
        e.preventDefault();
        if (e.key === ' ') {
          handleVirtualKeyPress('space');
        } else {
          handleVirtualKeyPress(e.key.toUpperCase());
        }
        return;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paymentPopupOpen, activeInput]);

  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [formValues, setFormValues] = useState(initialFormValues);
  const [mediaFiles, setMediaFiles] = useState({ newFiles: [] });
  const [removedMediaUrls, setRemovedMediaUrls] = useState([]); // store urls of existing media removed in edit
  const [existingMedia, setExistingMedia] = useState([]);
  const combinedMedia = useMemo(() => {
    return (existingMedia || []).concat(mediaFiles.newFiles || []);
  }, [existingMedia, mediaFiles.newFiles]);
  const [editingGame, setEditingGame] = useState(null);
  const [filters, setFilters] = useState({ search: '', status: 'all', category: 'all' });
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmIsDestructive, setConfirmIsDestructive] = useState(false);

  const currentAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem('admin');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const canEdit = currentAdmin && (currentAdmin.role === 1 || currentAdmin.role === '1');
  const canViewBadges = currentAdmin && (currentAdmin.role === 1 || currentAdmin.role === '2' || currentAdmin.role === '1' || currentAdmin.role === '2');
  const isPrivileged = currentAdmin && (currentAdmin.role === 1 || currentAdmin.role === '1' || currentAdmin.role === 2 || currentAdmin.role === '2');

  const limit = 200;

  const [cartItems, setCartItems] = useState([]);
  const [cartCollapsed, setCartCollapsed] = useState(false);
  const [selectedDurations, setSelectedDurations] = useState({}); // Track selected duration for each game
  const [selectedParticipants, setSelectedParticipants] = useState({}); // Track selected participants for each game
  const [participantSelecting, setParticipantSelecting] = useState({}); // whether user is choosing participants for a game
  const [categories, setCategories] = useState([]);
  const [challengeTypes, setChallengeTypes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedChallengeType, setSelectedChallengeType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [activeTab, setActiveTab] = useState('games');
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile to set default cart state
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Default cart closed on mobile, open on desktop
  useEffect(() => {
    // On mobile: collapsed by default; on desktop: open by default (for all routes)
    setCartCollapsed(isMobile);
  }, [isMobile, isPublicRoute]);

  const addToCart = (game) => {
    const selectedDuration = selectedDurations[game._id] || (game.units === 'per_over' ? '5overs' : 'default');
    let price = 0;
    let durationLabel = '';
    
    // Check if this is a challenge game with price per participant
    const isChallengeGame = game.challenge?.type && game.challenge?.price_per_participant;
    
    if (isChallengeGame) {
      // For challenge games, calculate price based on selected participants
      const participants = selectedParticipants[game._id] || 1;
      const pricePerParticipant = Number(game.challenge.price_per_participant);
      price = pricePerParticipant * participants;
      durationLabel = `${participants} participant${participants > 1 ? 's' : ''}`;
    } else {
      // For regular games, use existing logic
      switch (selectedDuration) {
        case '30min':
          price = Number(game.charges_30min || 0);
          durationLabel = '30 minutes';
          break;
        case '1hr':
          price = Number(game.charges_1hr || 0);
          durationLabel = '1 hour';
          break;
        case '2hr':
          price = Number(game.charges_2hr || 0);
          durationLabel = '2 hours';
          break;
        case '5overs':
          price = Number(game.charges_5overs || 0);
          durationLabel = '5 overs';
          break;
        case '10overs':
          price = Number(game.charges_10overs || 0);
          durationLabel = '10 overs';
          break;
        case '20overs':
          price = Number(game.charges_20overs || 0);
          durationLabel = '20 overs';
          break;
        default:
          price = Number(game.charges_default || game.charges_1hr || 0);
          if (game.units === 'minutes' || game.units === 'hours') {
            // Convert duration to user-friendly format for cart
            if (game.duration && game.units) {
              if (game.units === 'minutes') {
                if (game.duration === 60) {
                  durationLabel = '1 hour';
                } else if (game.duration === 30) {
                  durationLabel = '30 minutes';
                } else if (game.duration === 120) {
                  durationLabel = '2 hours';
                } else {
                  durationLabel = `${game.duration} minutes`;
                }
              } else if (game.units === 'hours') {
                if (game.duration === 1) {
                  durationLabel = '1 hour';
                } else if (game.duration === 2) {
                  durationLabel = '2 hours';
                } else {
                  durationLabel = `${game.duration} hour${game.duration > 1 ? 's' : ''}`;
                }
              }
            } else {
              durationLabel = 'Default';
            }
          } else {
            const unitLabels = {
              'per_game': 'Per Game',
              'per_over': 'Per Over', 
              'per_ride': 'Per Ride'
            };
            durationLabel = unitLabels[game.units] || 'Default';
          }
      }
    }
    
    // pick first available media for thumbnail
    let thumbUrl = placeholderImage;
    let mediaType = 'image';
    if (Array.isArray(game.media) && game.media.length > 0) {
      const m = game.media.find((x) => x && x.url);
      if (m) {
        thumbUrl = m.url;
        mediaType = m.type === 'video' ? 'video' : 'image';
      }
    }

    setCartItems((prev) => [
      ...prev,
      {
        id: game._id,
        name: game.name,
        duration: durationLabel,
        price,
        thumbUrl,
        mediaType,
        selectedDuration
      }
    ]);
  };

  const removeFromCart = (id) => {
    setCartItems((prev) => prev.filter((it, idx) => it.id !== id || idx !== prev.findIndex((p) => p.id === id)));
  };

  const handleDurationChange = (gameId, duration) => {
    setSelectedDurations(prev => ({
      ...prev,
      [gameId]: duration
    }));
  };

  const handleParticipantChange = (gameId, change) => {
    setSelectedParticipants(prev => {
      const current = prev[gameId] || 1;
      const newValue = Math.max(1, Math.min(100, current + change));
      return {
        ...prev,
        [gameId]: newValue
      };
    });
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleChallengeTypeChange = (challengeType) => {
    setSelectedChallengeType(challengeType);
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const getAvailableDurations = (game) => {
    const durations = [];
    
    // Check if this is a challenge game with price per participant
    const isChallengeGame = game.challenge?.type && game.challenge?.price_per_participant;
    
    if (isChallengeGame) {
      // For challenge games, don't show dropdown - use plus/minus buttons instead
      return [];
    } else {
      // For regular games, use existing logic
      // For time-based units (minutes, hours), show duration options
      if (game.units === 'minutes' || game.units === 'hours') {
        // Always include default duration with user-friendly labels
        if (game.charges_default || game.charges_1hr) {
          let defaultLabel = 'Default';
          
          // Convert duration to user-friendly format
          if (game.duration && game.units) {
            if (game.units === 'minutes') {
              if (game.duration === 60) {
                defaultLabel = '1 hour';
              } else if (game.duration === 30) {
                defaultLabel = '30 minutes';
              } else if (game.duration === 120) {
                defaultLabel = '2 hours';
              } else {
                defaultLabel = `${game.duration} minutes`;
              }
            } else if (game.units === 'hours') {
              if (game.duration === 1) {
                defaultLabel = '1 hour';
              } else if (game.duration === 2) {
                defaultLabel = '2 hours';
              } else {
                defaultLabel = `${game.duration} hour${game.duration > 1 ? 's' : ''}`;
              }
            }
          }
          
          durations.push({
            value: 'default',
            label: defaultLabel,
            price: Number(game.charges_default || game.charges_1hr || 0)
          });
        }
        
        // Add other durations if they have prices
        if (game.charges_30min && Number(game.charges_30min) > 0) {
          durations.push({
            value: '30min',
            label: '30 minutes',
            price: Number(game.charges_30min)
          });
        }
        
        if (game.charges_1hr && Number(game.charges_1hr) > 0 && !game.charges_default) {
          durations.push({
            value: '1hr',
            label: '1 hour',
            price: Number(game.charges_1hr)
          });
        }
        
        if (game.charges_2hr && Number(game.charges_2hr) > 0) {
          durations.push({
            value: '2hr',
            label: '2 hours',
            price: Number(game.charges_2hr)
          });
        }
      } else {
        // For non-time-based units (per_game, per_over, per_ride)
        if (game.units === 'per_over') {
          // show per-over options if provided
          if (game.charges_5overs && Number(game.charges_5overs) > 0) {
            durations.push({ value: '5overs', label: '5 overs', price: Number(game.charges_5overs) });
          }
          if (game.charges_10overs && Number(game.charges_10overs) > 0) {
            durations.push({ value: '10overs', label: '10 overs', price: Number(game.charges_10overs) });
          }
          if (game.charges_20overs && Number(game.charges_20overs) > 0) {
            durations.push({ value: '20overs', label: '20 overs', price: Number(game.charges_20overs) });
          }

          // show only per-over options (5,10,20) for per_over units
        } else {
          if (game.charges_default) {
            const unitLabels = {
              'per_game': 'Per Game',
              'per_over': 'Per Over', 
              'per_ride': 'Per Ride'
            };
            durations.push({
              value: 'default',
              label: unitLabels[game.units] || 'Default',
              price: Number(game.charges_default)
            });
          }
        }
      }
    }
    
    return durations;
  };

  const getCurrentPrice = (game) => {
    const selectedDuration = selectedDurations[game._id] || (game.units === 'per_over' ? '5overs' : 'default');
    
    // Check if this is a challenge game with price per participant
    const isChallengeGame = game.challenge?.type && game.challenge?.price_per_participant;
    
    if (isChallengeGame) {
      // For challenge games, calculate price based on selected participants
      const participants = selectedParticipants[game._id] || 1;
      const pricePerParticipant = Number(game.challenge.price_per_participant);
      return pricePerParticipant * participants;
    }
    
    // For regular games, use existing logic
    switch (selectedDuration) {
      case '30min':
        return Number(game.charges_30min || 0);
      case '1hr':
        return Number(game.charges_1hr || 0);
      case '2hr':
        return Number(game.charges_2hr || 0);
      case '5overs':
        return Number(game.charges_5overs || 0);
      case '10overs':
        return Number(game.charges_10overs || 0);
      case '20overs':
        return Number(game.charges_20overs || 0);
      default:
        return Number(game.charges_default || game.charges_1hr || 0);
    }
  };

  const shouldShowTimeBasedFields = (units) => {
    return units === 'minutes' || units === 'hours';
  };

  // Separate games and challenges
  const regularGames = games.filter(game => !game.challenge?.type);
  const challengeGames = games.filter(game => game.challenge?.type);

  // Filter games based on selected filters
  const filteredGames = regularGames.filter(game => {
    // Category filter
    if (selectedCategory !== 'all' && game.category !== selectedCategory) {
      return false;
    }
    
    // Status filter
    if (selectedStatus !== 'all' && game.status !== selectedStatus) {
      return false;
    }
    
    return true;
  });

  // Prefer trending games first (either `istrending` or legacy `is_trending`)
  const sortByTrending = (arr) => {
    return arr.slice().sort((a, b) => {
      const ta = !!(a.istrending || a.is_trending);
      const tb = !!(b.istrending || b.is_trending);
      return (tb === ta) ? 0 : (tb ? 1 : -1);
    });
  };

  const filteredGamesSorted = sortByTrending(filteredGames);

  // Filter challenges based on selected filters
  const filteredChallenges = challengeGames.filter(game => {
    // Challenge type filter
    if (selectedChallengeType !== 'all' && game.challenge?.type !== selectedChallengeType) {
      return false;
    }
    
    // Status filter
    if (selectedStatus !== 'all' && game.status !== selectedStatus) {
      return false;
    }
    
    return true;
  });

  const filteredChallengesSorted = sortByTrending(filteredChallenges);

  // Get current items based on active tab
  const currentItems = activeTab === 'games' ? filteredGamesSorted : 
                      activeTab === 'challenges' ? filteredChallengesSorted : 
                      []; // Top Players will be empty for now

  // Show category badge only when "All" is selected
  const shouldShowCategoryBadge = selectedCategory === 'all';

  const subtotal = cartItems.reduce((s, it) => s + Number(it.price || 0), 0);
  const tax = 0;
  const total = +(subtotal + tax).toFixed(2);
  const canProceedToPayment = userPhone.trim().length === 10 && (foundUser || userName.trim().length > 0);

  // User search functionality
  const searchUserByPhone = useCallback(async (phone) => {
    if (!phone || phone.trim().length < 10) {
      setFoundUser(null);
      setUserSearchError('Please enter a valid phone number');
      return;
    }

    setSearchingUser(true);
    setUserSearchError(null);
    
    try {
      const result = await listUsers({ search: phone.trim(), limit: 1 });
      const users = result.data || [];
      
      if (users.length > 0) {
        const user = users[0];
        setFoundUser(user);
        setUserName(user.name || '');
        setUserSearchError(null);
      } else {
        setFoundUser(null);
        setUserName('');
        setUserSearchError(null);
      }
    } catch (err) {
      setFoundUser(null);
      setUserSearchError(err.message || 'Failed to search user');
    } finally {
      setSearchingUser(false);
    }
  }, []);

  const handlePhoneChange = (e) => {
    const phone = e.target.value;
    
    // Limit to 10 digits only
    if (phone.length <= 10) {
      setUserPhone(phone);
      
      // Auto-search when phone number is complete (exactly 10 digits)
      if (phone.length === 10) {
        searchUserByPhone(phone);
      } else {
        setFoundUser(null);
        setUserName('');
        setUserSearchError(null);
      }
    }
  };

  const openPaymentPopup = () => {
    if (cartItems.length === 0) {
      setFeedback({ type: 'error', message: 'Cart is empty. Add some games first.' });
      return;
    }
    setPaymentPopupOpen(true);
    setUserPhone('');
    setUserName('');
    setFoundUser(null);
    setUserSearchError(null);
    setProceedHint('');
    setPaymentProcessed(false);
    // open number pad by default when payment popup opens
    setVirtualKeyboardOpen(true);
    setKeyboardLayout('phone');
    setActiveInput('phone');
  };

  const closePaymentPopup = () => {
    setPaymentPopupOpen(false);
    setUserPhone('');
    setUserName('');
    setFoundUser(null);
    setUserSearchError(null);
    setProceedHint('');
    setPaymentProcessed(false);
  };

  const handlePayment = async (paymentMethod) => {
    if (!userPhone.trim()) {
      setUserSearchError('Phone number is required');
      return;
    }

    if (!foundUser && !userName.trim()) {
      setUserSearchError('Name is required for new users');
      return;
    }

    // For "Pay at Counter", open POS printer modal
    if (paymentMethod === 'Pay at Counter') {
      setSelectedPaymentMethod(paymentMethod);
      setPosPrinterOpen(true);
      return;
    }

    // For other payment methods, process directly
    await processPayment(paymentMethod);
  };

  const processPayment = async (paymentMethod, printStatus = 'later') => {
    try {
      if (paymentProcessed) return; // prevent duplicate transactions
      // If user not found, create new user
      if (!foundUser) {
        await createUser({ name: userName.trim(), phone: userPhone.trim() });
      }

      // Create transaction
      const userData = {
        name: foundUser ? foundUser.name : userName.trim(),
        phone: userPhone.trim()
      };

      const transactionData = createTransactionPayload(cartItems, userData, paymentMethod, total);
      // If creating transaction to show QR / complete-payment (later), mark it as pending
      if (printStatus === 'later') {
        try {
          transactionData.payment_status = 'pending';
        } catch (e) {
          // ignore if payload is immutable
        }
      }
      const transactionResult = await createTransaction(transactionData);

      // Show success message with transaction ID
      const printMessage = printStatus === 'now' ? ' and receipt printed' : '';
      setFeedback({ 
        type: 'success', 
        message: `Payment processed via ${paymentMethod}${printMessage}. Transaction ID: ${transactionResult.transactionId}. Total: ${formatCurrency(total)}` 
      });
      setPaymentProcessed(true);

      // Save receipt data so we can print or show QR after transaction
      setLastReceipt({
        cartItems: cartItems.slice(),
        userData,
        total,
        subtotal,
        tax,
        transactionId: transactionResult.transactionId,
        paymentMethod,
        payment_status: printStatus === 'later' ? 'pending' : transactionResult.payment_status || 'pending'
      });
      
      // Clear cart and close popup
      setCartItems([]);
      closePaymentPopup();
      
      // After creating transaction, show paymentOptions (QR + cash option) for 30s unless caller requested immediate print
      if (printStatus === 'later') {
        setPaymentOptionsOpen(true);
        setPaymentTimer(30);
        setTimerActive(true);
      }
      
    } catch (err) {
      setUserSearchError(err.message || 'Payment failed');
    }
  };

  const handlePrintComplete = async (printStatus) => {
    // Only process payment once. If already processed, just close the printer.
    if (paymentProcessed) {
      setPosPrinterOpen(false);
      setSelectedPaymentMethod(null);
      return;
    }
    await processPayment(selectedPaymentMethod, printStatus);
    setPosPrinterOpen(false);
    setSelectedPaymentMethod(null);
  };

  // Timer logic for payment options
  useEffect(() => {
    let interval = null;
    if (timerActive && paymentTimer > 0) {
      interval = setInterval(() => {
        setPaymentTimer(timer => timer - 1);
      }, 1000);
    } else if (paymentTimer === 0) {
      setPaymentOptionsOpen(false);
      setTimerActive(false);
      setPaymentTimer(40);
    }
    return () => clearInterval(interval);
  }, [timerActive, paymentTimer]);

  const openPaymentOptions = () => {
    setPaymentOptionsOpen(true);
    setTimerActive(true);
    setPaymentTimer(40);
  };

  const closePaymentOptions = () => {
    setPaymentOptionsOpen(false);
    setTimerActive(false);
    setPaymentTimer(40);
  };

  // Virtual keyboard functions
  const openVirtualKeyboard = (inputType) => {
    setActiveInput(inputType);
    setKeyboardLayout(inputType === 'phone' ? 'phone' : 'text');
    setVirtualKeyboardOpen(true);
  };

  const closeVirtualKeyboard = () => {
    setVirtualKeyboardOpen(false);
    setActiveInput(null);
  };

  const handleVirtualKeyPress = (key) => {
    if (activeInput === 'phone') {
      if (key === 'backspace') {
        setUserPhone(prev => prev.slice(0, -1));
      } else if (key === 'clear') {
        setUserPhone('');
      } else if (userPhone.length < 10) { // Limit phone length to 10 digits
        const newPhone = userPhone + key;
        setUserPhone(newPhone);
        
        // Auto-search when 10 digits are entered
        if (newPhone.length === 10) {
          searchUserByPhone(newPhone);
        }
      }
    } else if (activeInput === 'name') {
      if (key === 'backspace') {
        setUserName(prev => prev.slice(0, -1));
      } else if (key === 'clear') {
        setUserName('');
      } else if (key === 'space') {
        setUserName(prev => prev + ' ');
      } else if (userName.length < 50) { // Limit name length
        setUserName(prev => prev + key);
      }
    }
  };

  // Phone keyboard layout
  const phoneKeys = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['+', '0', 'backspace']
  ];

  // Text keyboard layout
  const textKeys = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
    ['space', 'backspace', 'clear']
  ];

  const resetForm = useCallback(() => {
    setFormValues(initialFormValues);
    setMediaFiles({ newFiles: [] });
    setRemovedMediaUrls([]);
    setExistingMedia([]);
    setEditingGame(null);
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await fetchCategoriesController();
      if (data && data.success) {
        setCategories(data.data.categories || []);
        setChallengeTypes(data.data.challenge_types || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, []);

  const fetchGames = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listGames({ page: 1, limit });
      // Support different backend response shapes:
      // - { success: true, data: [...] }  (wrapped)
      // - [...] (raw array)
      // - { data: { data: [...] } } (nested)
      let rows = [];
      if (Array.isArray(result)) {
        rows = result;
      } else if (Array.isArray(result.data)) {
        rows = result.data;
      } else if (Array.isArray(result.data?.data)) {
        rows = result.data.data;
      } else {
        // fallback: try common keys
        rows = Array.isArray(result.games) ? result.games : [];
      }
      setGames(rows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchGames();
    fetchCategories();
  }, [fetchGames, fetchCategories]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timeout = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timeout);
  }, [feedback]);


  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    setMediaFiles((prev) => ({ ...prev, newFiles: (prev.newFiles || []).concat(newFiles) }));
  };


  const handleToggleForm = () => {
    if (formOpen) {
      resetForm();
      setFormOpen(false);
    } else {
      resetForm();
      setFormOpen(true);
    }
  };

  const buildFormData = () => {
    const data = new FormData();

    // Prepare values and prefer custom inputs when provided
    const valuesToSubmit = { ...formValues };
    if (valuesToSubmit.category === 'other' && valuesToSubmit.category_custom) {
      valuesToSubmit.category = valuesToSubmit.category_custom;
    }
    // remove helper keys so they are not sent twice
    delete valuesToSubmit.category_custom;

    if (valuesToSubmit.challenge_type === 'other' && valuesToSubmit.challenge_type_custom) {
      valuesToSubmit.challenge_type = valuesToSubmit.challenge_type_custom;
    }
    delete valuesToSubmit.challenge_type_custom;

    Object.entries(valuesToSubmit).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) return;
      if (key === 'challenge_participants') {
        // Skip participants field - it's read-only and shouldn't be submitted
        return;
      }
      // include boolean for istrending
      if (key === 'istrending') {
        data.append(key, value ? 'true' : 'false');
        return;
      }
      if (key === 'challenge_start_date' || key === 'challenge_end_date') {
        const isoValue = toIsoString(value);
        if (isoValue) data.append(key, isoValue);
        return;
      }
      data.append(key, value);
    });

    // append any newly selected files (images/videos)
    (mediaFiles.newFiles || []).forEach((f, idx) => {
      // name them media_new[] so backend can accept array of files
      data.append(editingGame ? 'media_new[]' : `media[]`, f);
    });

    // if editing, include list of removed media urls so server can delete them
    if (editingGame && removedMediaUrls.length > 0) {
      // append legacy and alternative keys to support different backend expectations
      removedMediaUrls.forEach((u) => {
        data.append('removed_media[]', u);
        data.append('removed_media_urls[]', u);
      });
    }

    // if editing and user removed all existing media and didn't add any new files,
    // explicitly tell backend to clear media (helpful if backend ignores removed list)
    if (editingGame && (existingMedia.length === 0) && ((mediaFiles.newFiles || []).length === 0)) {
      data.append('clear_media', 'true');
    }

    return data;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // show confirmation modal before proceed
    setConfirmMessage(editingGame ? 'Are you sure you want to update this game?' : 'Are you sure you want to create this game?');
    setConfirmIsDestructive(false);
    setConfirmAction(() => async () => {
      setSubmitting(true);
      setFeedback(null);
      try {
        const formData = buildFormData();
        if (editingGame) {
          await updateGame(editingGame._id, formData);
          setFeedback({ type: 'success', message: 'Game updated successfully.' });
        } else {
          await createGame(formData);
          setFeedback({ type: 'success', message: 'Game created successfully.' });
        }
        resetForm();
        setFormOpen(false);
        await fetchGames();
      } catch (err) {
        setFeedback({ type: 'error', message: err.message });
      } finally {
        setSubmitting(false);
      }
    });
    setConfirmOpen(true);
  };

  const handleEdit = (game) => {
    setFormOpen(true);
    setEditingGame(game);
    setFormValues({
      name: game.name || '',
      description: game.description || '',
      category: game.category || '',
      istrending: !!game.istrending || !!game.is_trending,
      category_custom: '',
      duration: game.duration ?? '',
      charges_default: game.charges_default ?? '',
      charges_30min: game.charges_30min ?? '',
      charges_1hr: game.charges_1hr ?? '',
      charges_2hr: game.charges_2hr ?? '',
      charges_5overs: game.charges_5overs ?? '',
      charges_10overs: game.charges_10overs ?? '',
      charges_20overs: game.charges_20overs ?? '',
      units: game.units || 'minutes',
      status: game.status || 'active',
      challenge_type: game.challenge?.type || '',
      challenge_type_custom: '',
      challenge_start_date: toDateTimeLocal(game.challenge?.start_date),
      challenge_end_date: toDateTimeLocal(game.challenge?.end_date),
      challenge_total_participants: game.challenge?.total_participants ?? '',
      challenge_winner_reward: game.challenge?.winner_reward ?? '',
      challenge_price_per_participant: game.challenge?.price_per_participant ?? '',
      challenge_participants: Array.isArray(game.challenge?.participants)
        ? game.challenge.participants.join(', ')
        : ''
    });
    setMediaFiles({ imageFile: null, videoFile: null });
    // populate existing media urls for edit
    setExistingMedia(Array.isArray(game.media) ? game.media.filter(m => m && m.url) : []);
  };

  const handleCancelEdit = () => {
    resetForm();
    setFormOpen(false);
  };

  const handleDelete = async (game) => {
    // show custom confirmation modal
    setConfirmMessage(`Are you sure you want to delete "${game.name}"?`);
    setConfirmIsDestructive(true);
    setConfirmAction(() => async () => {
      setSubmitting(true);
      setFeedback(null);
      try {
        await deleteGame(game._id);
        setFeedback({ type: 'success', message: 'Game deleted successfully.' });
        await fetchGames();
        // close form if we deleted the currently editing game
        if (editingGame && editingGame._id === game._id) {
          resetForm();
          setFormOpen(false);
        }
      } catch (err) {
        setFeedback({ type: 'error', message: err.message });
      } finally {
        setSubmitting(false);
      }
    });
    setConfirmOpen(true);
  };

  const refreshFilters = () => {
    setFilters({ search: '', status: 'all', category: 'all' });
    fetchGames();
  };

  const formTitle = editingGame ? 'Update Game' : 'Create New Game';
  const formSubtitle = editingGame
    ? `Editing ${editingGame.name}`
    : 'Fill in the details to add a new game to the catalog.';

  // Add cart-collapsed class to the main content when cart is collapsed
  useEffect(() => {
    const mainContent = document.querySelector('.admin-content.games-page');
    if (mainContent) {
      if (cartCollapsed) {
        mainContent.classList.add('cart-collapsed');
      } else {
        mainContent.classList.remove('cart-collapsed');
      }
    }
    
    // Cleanup on unmount
    return () => {
      const mainContent = document.querySelector('.admin-content.games-page');
      if (mainContent) {
        mainContent.classList.remove('cart-collapsed');
      }
    };
  }, [cartCollapsed]);

  const handleProceedToPayment = async () => {
    // Validate input before proceeding
    if (!canProceedToPayment) {
      setProceedHint('Please enter the required details above before proceeding.');
      return;
    }
    setProceedHint('');

    // Create transaction and then show QR/Complete Payment modal for 30s
    await processPayment('QR', 'later');
  };

  return (
    <div>

      {feedback && (
        <div className={`alert ${feedback.type === 'error' ? 'alert-error' : 'alert-success'}`}>
          {feedback.message}
        </div>
      )}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Floating Cart Button (Mobile) */}
      {isMobile && (
        <button
          type="button"
          aria-label="Open booking cart"
          onClick={() => setCartCollapsed(false)}
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 10003,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 44,
            height: 44,
            borderRadius: 22,
            background: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            color: 'var(--text-primary)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
            cursor: 'pointer'
          }}
        >
          <span style={{ fontSize: 20 }}>🛒</span>
          {cartItems.length > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              background: 'var(--accent)',
              color: '#0b1735',
              borderRadius: 10,
              padding: '1px 6px',
              fontSize: 12,
              fontWeight: 700,
              border: '1px solid rgba(255,255,255,0.2)'
            }}>{cartItems.length}</span>
          )}
        </button>
      )}

      {/* Top Header with Filters */}
      <div style={{
        background: 'var(--bg)',
        padding: '20px 0',
        marginBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: isPublicRoute ? 'center' : 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isPublicRoute ? 'center' : 'flex-start'
            }}>
              <img 
                src="/Gaming Science logo.webp" 
                alt="Gaming Science" 
                style={{
                  height: '45px',
                  width: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
          </div>
          {/* Mobile Cart Icon (header) - kept hidden because we have floating button */}
          <div style={{ display: 'none' }} />

          {/* Only show controls for admin routes */}
          {!isPublicRoute && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              color: 'var(--text-secondary)',
              fontSize: '0.9rem'
            }}>
              {/* Fullscreen toggle available to all users */}
              <div style={{ display: isMobile ? 'none' : 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                 type="button"
                 className="button-secondary"
                 onClick={() => {
                   try {
                     if (!document.fullscreenElement) {
                       document.documentElement.requestFullscreen?.();
                     } else {
                       document.exitFullscreen?.();
                     }
                   } catch (e) {
                     /* ignore */
                   }
                 }}
                 title="Toggle Fullscreen"
                 style={{ marginLeft: 8, minWidth: 48, padding: '6px 12px' }}
               >
                 ⛶
               </button>
               {canEdit && (
                 <>
                   <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Status:</span>
                   <select
                     value={selectedStatus}
                     onChange={(e) => handleStatusChange(e.target.value)}
                     style={{
                       padding: '6px 12px',
                       borderRadius: 'var(--border-radius-sm)',
                       border: '1px solid var(--card-border)',
                       background: 'var(--input-bg)',
                       color: 'var(--text-primary)',
                       fontSize: '0.85rem',
                       cursor: 'pointer'
                     }}
                   >
                     <option value="all">All</option>
                     <option value="active">Active</option>
                     <option value="inactive">Inactive</option>
                   </select>
                 </>
               )}
              </div>
              
              {/* Create Game Button - Only for roles 1 and 2 */}
              {canEdit && (
                <>
                <button
                  type="button"
                  className="button-primary"
                  onClick={handleToggleForm}
                  style={{
                    display: isMobile ? 'none' : 'inline-flex',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    fontWeight: '500'
                  }}
                >
                  {formOpen ? 'Close Form' : 'Create Game'}
                </button>
                
                </>
              )}
            </div>
          )}
        </div>

        {/* Public fullscreen toggle for /games (sticky, low-opacity for kiosk) */}
        {isPublicRoute && !isMobile && (
          <button
            type="button"
            onClick={() => {
              try {
                if (!document.fullscreenElement) {
                  document.documentElement.requestFullscreen?.();
                } else {
                  document.exitFullscreen?.();
                }
              } catch (e) {
                /* ignore */
              }
            }}
            title="Toggle Fullscreen"
            aria-label="Toggle Fullscreen"
            style={{
              position: 'fixed',
              top: 12,
              right: 12,
              zIndex: 14000,
              minWidth: 72,
              minHeight: 48,
              padding: '8px 16px',
              borderRadius: 10,
              background: 'rgba(0,0,0,0.28)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
              opacity: 0,
              transition: 'opacity 0.12s ease',
              cursor: 'pointer'
            }}
          >
            ⛶
          </button>
        )}

        {/* Mobile Admin Controls (roles 1/2) */}
        {!isPublicRoute && isMobile && canEdit && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 12px',
            marginBottom: 12,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Status:</span>
              <select
                value={selectedStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '10px',
                  border: '1px solid var(--card-border)',
                  background: 'var(--input-bg)',
                  color: 'var(--text-primary)',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <button
              type="button"
              className="button-primary"
              onClick={handleToggleForm}
              style={{
                padding: '8px 12px',
                fontSize: '0.85rem',
                fontWeight: 600,
                flex: '0 0 auto'
              }}
            >
              {formOpen ? 'Close Form' : 'Create Game'}
            </button>
          </div>
        )}

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: isMobile ? '8px' : '12px',
          marginBottom: '24px',
          alignItems: 'center',
          justifyContent: isMobile ? 'flex-start' : 'center',
          width: '100%',
          overflowX: isMobile ? 'auto' : 'visible',
          padding: isMobile ? '0 8px' : undefined,
          WebkitOverflowScrolling: 'touch'
        }}>
          <button
            onClick={() => handleTabChange('games')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 20px',
              border: 'none',
              background: activeTab === 'games' ? 'var(--accent)' : 'var(--input-bg)',
              color: activeTab === 'games' ? '#0b1735' : 'var(--text-primary)',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: activeTab === 'games' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: isMobile ? 'auto' : '120px',
              justifyContent: 'center',
              flex: '0 0 auto'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🎯</span>
            Games
          </button>
          <button
            onClick={() => handleTabChange('challenges')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 20px',
              border: 'none',
              background: activeTab === 'challenges' ? 'var(--accent)' : 'var(--input-bg)',
              color: activeTab === 'challenges' ? '#0b1735' : 'var(--text-primary)',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: activeTab === 'challenges' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: isMobile ? 'auto' : '120px',
              justifyContent: 'center',
              flex: '0 0 auto'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>🏆</span>
            Challenges
          </button>
          <button
            onClick={() => handleTabChange('top-players')}
            style={{
              padding: isMobile ? '10px 14px' : '12px 20px',
              border: 'none',
              background: activeTab === 'top-players' ? 'var(--accent)' : 'var(--input-bg)',
              color: activeTab === 'top-players' ? '#0b1735' : 'var(--text-primary)',
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              fontWeight: activeTab === 'top-players' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '16px',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              minWidth: isMobile ? 'auto' : '120px',
              justifyContent: 'center',
              flex: '0 0 auto'
            }}
          >
            <span style={{ fontSize: '1.1rem' }}>👑</span>
            Top Players
          </button>
        </div>

        {/* Filters Section */}
        <div style={{
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          {/* Show different filters based on active tab */}
          {activeTab === 'games' && (
            <>
              {/* Category Filters for Games */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => handleCategoryChange('all')}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 'var(--border-radius-sm)',
                    border: 'none',
                    background: selectedCategory === 'all' ? 'var(--accent)' : 'var(--input-bg)',
                    color: selectedCategory === 'all' ? '#0b1735' : 'var(--text-primary)',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  All
                </button>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => handleCategoryChange(category)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 'var(--border-radius-sm)',
                      border: 'none',
                      background: selectedCategory === category ? 'var(--accent)' : 'var(--input-bg)',
                      color: selectedCategory === category ? '#0b1735' : 'var(--text-primary)',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textTransform: 'capitalize'
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </>
          )}

          {activeTab === 'challenges' && (
            <>
              {/* Challenge Type Filters */}
              {challengeTypes.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    onClick={() => handleChallengeTypeChange('all')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 'var(--border-radius-sm)',
                      border: 'none',
                      background: selectedChallengeType === 'all' ? 'var(--accent)' : 'var(--input-bg)',
                      color: selectedChallengeType === 'all' ? '#0b1735' : 'var(--text-primary)',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    All
                  </button>
                  {challengeTypes.map((challengeType) => (
                    <button
                      key={challengeType}
                      onClick={() => handleChallengeTypeChange(challengeType)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 'var(--border-radius-sm)',
                        border: 'none',
                        background: selectedChallengeType === challengeType ? 'var(--accent)' : 'var(--input-bg)',
                        color: selectedChallengeType === challengeType ? '#0b1735' : 'var(--text-primary)',
                        fontSize: '0.9rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textTransform: 'capitalize'
                      }}
                    >
                      {challengeType}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* Create Game Form - Hidden by default, shown when needed */}
      {formOpen && (
        <section className="game-form-wrapper open" style={{ marginBottom: '24px' }}>
        <div className="game-form-header">
          <div>
            <h2>{formTitle}</h2>
            <p className="game-form-subtitle">{formSubtitle}</p>
          </div>
          <div className="game-form-header-actions">
            {editingGame && (
              <button
                type="button"
                className="button-secondary"
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                Cancel Edit
              </button>
            )}
            <button
              type="button"
                className="button-secondary"
              onClick={handleToggleForm}
            >
                Close Form
            </button>
          </div>
        </div>

          <form className="game-form-body" onSubmit={handleSubmit}>
            <div className="game-form-group">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                placeholder="Enter game name"
                required
              />
            </div>
            <div className="game-form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formValues.description}
                onChange={handleInputChange}
                placeholder="Enter game description"
                rows={3}
              />
            </div>
            <div className="game-form-half-row">
              <div className="game-form-group">
                <label htmlFor="category">Category</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    id="category"
                    name="category"
                    value={formValues.category}
                    onChange={handleInputChange}
                    style={{ flex: 1, padding: '8px 12px' }}
                  >
                    <option value="">Select category</option>
                    <option value="all">All</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    <option value="other">Other (custom)</option>
                  </select>
                  {formValues.category === 'other' && (
                    <input
                      id="category_custom"
                      name="category_custom"
                      value={formValues.category_custom}
                      onChange={handleInputChange}
                      placeholder="Enter custom category"
                      style={{ flex: 1, padding: '8px 12px' }}
                    />
                  )}
                </div>
              </div>
              <div className="game-form-group">
                <label htmlFor="status">Status</label>
                <select id="status" name="status" value={formValues.status} onChange={handleInputChange}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="game-form-group">
                <label htmlFor="units">Units</label>
                <select id="units" name="units" value={formValues.units} onChange={handleInputChange}>
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="per_game">Per Game</option>
                  <option value="per_over">Per Over</option>
                  <option value="per_ride">Per Ride</option>
                </select>
              </div>
            </div>
            <div className="game-form-half-row">
              {shouldShowTimeBasedFields(formValues.units) && (
              <div className="game-form-group">
                <label htmlFor="duration">Duration</label>
                <input
                  id="duration"
                  name="duration"
                  type="number"
                  min="0"
                  value={formValues.duration}
                  onChange={handleInputChange}
                  placeholder="Duration"
                />
              </div>
              )}
              <div className="game-form-group">
                <label htmlFor="charges_default">Default Charge</label>
                <input
                  id="charges_default"
                  name="charges_default"
                  type="number"
                  min="0"
                  value={formValues.charges_default}
                  onChange={handleInputChange}
                  placeholder="Default price"
                />
              </div>
              {shouldShowTimeBasedFields(formValues.units) && (
                <>
              <div className="game-form-group">
                <label htmlFor="charges_30min">30 Min Charge</label>
                <input
                  id="charges_30min"
                  name="charges_30min"
                  type="number"
                  min="0"
                  value={formValues.charges_30min}
                  onChange={handleInputChange}
                  placeholder="Charge for 30 minutes"
                />
              </div>
              <div className="game-form-group">
                <label htmlFor="charges_1hr">1 Hour Charge</label>
                <input
                  id="charges_1hr"
                  name="charges_1hr"
                  type="number"
                  min="0"
                  value={formValues.charges_1hr}
                  onChange={handleInputChange}
                  placeholder="Charge for 1 hour"
                />
              </div>
              <div className="game-form-group">
                <label htmlFor="charges_2hr">2 Hour Charge</label>
                <input
                  id="charges_2hr"
                  name="charges_2hr"
                  type="number"
                  min="0"
                  value={formValues.charges_2hr}
                  onChange={handleInputChange}
                  placeholder="Charge for 2 hours"
                />
              </div>
                </>
              )}
              {/* Per-over charges */}
              {formValues.units === 'per_over' && (
                <>
                  <div className="game-form-group">
                    <label htmlFor="charges_5overs">5 Overs Charge</label>
                <input
                  id="charges_5overs"
                  name="charges_5overs"
                  type="number"
                  min="0"
                  value={Number(formValues.charges_5overs || 0)}
                  onChange={(e) => handleInputChange({ target: { name: 'charges_5overs', value: Number(e.target.value) } })}
                  placeholder="Charge for 5 overs"
                />
                  </div>
                  <div className="game-form-group">
                    <label htmlFor="charges_10overs">10 Overs Charge</label>
                <input
                  id="charges_10overs"
                  name="charges_10overs"
                  type="number"
                  min="0"
                  value={Number(formValues.charges_10overs || 0)}
                  onChange={(e) => handleInputChange({ target: { name: 'charges_10overs', value: Number(e.target.value) } })}
                  placeholder="Charge for 10 overs"
                />
                  </div>
                  <div className="game-form-group">
                    <label htmlFor="charges_20overs">20 Overs Charge</label>
                <input
                  id="charges_20overs"
                  name="charges_20overs"
                  type="number"
                  min="0"
                  value={Number(formValues.charges_20overs || 0)}
                  onChange={(e) => handleInputChange({ target: { name: 'charges_20overs', value: Number(e.target.value) } })}
                  placeholder="Charge for 20 overs"
                />
                  </div>
                </>
              )}
            </div>
            <div className="form-section-title">Challenge Details</div>
            <div className="game-form-half-row">
              <div className="game-form-group">
                <label htmlFor="challenge_type">Challenge Category</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    id="challenge_type"
                    name="challenge_type"
                    value={formValues.challenge_type}
                    onChange={handleInputChange}
                    style={{ flex: 1, padding: '8px 12px' }}
                  >
                    <option value="">Select challenge type</option>
                    <option value="all">All</option>
                    {challengeTypes.map((ct) => (
                      <option key={ct} value={ct}>{ct}</option>
                    ))}
                    <option value="other">Other (custom)</option>
                  </select>
                  {formValues.challenge_type === 'other' && (
                    <input
                      id="challenge_type_custom"
                      name="challenge_type_custom"
                      value={formValues.challenge_type_custom}
                      onChange={handleInputChange}
                      placeholder="Enter custom challenge type"
                      style={{ flex: 1, padding: '8px 12px' }}
                    />
                  )}
                </div>
              </div>
              <div className="game-form-group">
                <label htmlFor="challenge_start_date">Start Date</label>
                <input
                  id="challenge_start_date"
                  name="challenge_start_date"
                  type="datetime-local"
                  value={formValues.challenge_start_date}
                  onChange={handleInputChange}
                />
              </div>
              <div className="game-form-group">
                <label htmlFor="challenge_end_date">End Date</label>
                <input
                  id="challenge_end_date"
                  name="challenge_end_date"
                  type="datetime-local"
                  value={formValues.challenge_end_date}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="game-form-half-row">
              <div className="game-form-group">
                <label htmlFor="challenge_total_participants">Total Participants</label>
                <input
                  id="challenge_total_participants"
                  name="challenge_total_participants"
                  type="number"
                  min="0"
                  value={formValues.challenge_total_participants}
                  onChange={handleInputChange}
                />
              </div>
              <div className="game-form-group">
                <label htmlFor="challenge_winner_reward">Winner Reward</label>
                <input
                  id="challenge_winner_reward"
                  name="challenge_winner_reward"
                  type="text"
                  value={formValues.challenge_winner_reward}
                  onChange={handleInputChange}
                  placeholder="e.g. Trophy, Cash Prize, Gift Card"
                />
              </div>
              <div className="game-form-group">
                <label htmlFor="challenge_price_per_participant">Price per Participant</label>
                <input
                  id="challenge_price_per_participant"
                  name="challenge_price_per_participant"
                  type="number"
                  min="0"
                  value={formValues.challenge_price_per_participant}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="game-form-group">
              <label htmlFor="challenge_participants">Participants (comma separated)</label>
              <input
                id="challenge_participants"
                name="challenge_participants"
                value={formValues.challenge_participants}
                readOnly
                placeholder="No participants"
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  color: 'var(--text-secondary)',
                  cursor: 'not-allowed'
                }}
              />
            </div>
            <div className="form-section-title">Media</div>
            <div className="game-form-half-row">
              <div className="game-form-group">
                <label htmlFor="mediaFiles">Media (images/videos)</label>
                <input
                  id="mediaFiles"
                  name="mediaFiles"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={handleFileChange}
                />
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {/* show previews for existing media when editing */}
                  {existingMedia.map((m, i) => (
                    <div key={m.url || i} style={{ position: 'relative' }}>
                      <button type="button" onClick={() => openMediaPreviewFromForm(existingMedia, i)} style={{ position: 'absolute', inset: 0, border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }} aria-label="Open preview" />
                      {m.type === 'video' ? (
                        <video src={m.url} width={96} height={60} style={{ objectFit: 'cover', borderRadius: 6 }} />
                      ) : (
                        <img src={m.url} width={96} height={60} style={{ objectFit: 'cover', borderRadius: 6 }} alt="media" />
                      )}
                      <button type="button" onClick={() => {
                        setRemovedMediaUrls(prev => prev.concat(m.url));
                        setExistingMedia(prev => prev.filter(x => x.url !== m.url));
                      }} style={{ position: 'absolute', top: -6, right: -6, background: '#ff6363', color: '#fff', border: 'none', borderRadius: 12, width: 24, height: 24, cursor: 'pointer' }}>✕</button>
                    </div>
                  ))}

                  {/* show previews for newly selected files */}
                  {(mediaFiles.newFiles || []).map((f, idx) => {
                    const url = f instanceof File ? URL.createObjectURL(f) : null;
                    const isVideo = f.type && f.type.startsWith('video');
                    return (
                      <div key={idx} style={{ position: 'relative' }}>
                        <button type="button" onClick={() => openMediaPreviewFromForm(mediaFiles.newFiles, idx)} style={{ position: 'absolute', inset: 0, border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' }} aria-label="Open preview" />
                        {isVideo ? (
                          <video src={url} width={96} height={60} style={{ objectFit: 'cover', borderRadius: 6 }} />
                        ) : (
                          <img src={url} width={96} height={60} style={{ objectFit: 'cover', borderRadius: 6 }} alt="new-media" />
                        )}
                        <button type="button" onClick={() => setMediaFiles(prev => ({ ...prev, newFiles: prev.newFiles.filter((_, i) => i !== idx) }))} style={{ position: 'absolute', top: -6, right: -6, background: '#ff6363', color: '#fff', border: 'none', borderRadius: 12, width: 24, height: 24, cursor: 'pointer' }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="game-form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label htmlFor="istrending" style={{ margin: 0 }}>Trending</label>
                <input
                  id="istrending"
                  name="istrending"
                  type="checkbox"
                  checked={!!formValues.istrending}
                  onChange={(e) => setFormValues((prev) => ({ ...prev, istrending: e.target.checked }))}
                />
              </div>
            </div>
            <div className="game-form-actions">
              {editingGame && (
                <>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={handleCancelEdit}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button-danger"
                  onClick={() => handleDelete(editingGame)}
                  disabled={submitting}
                  style={{ marginLeft: 8 }}
                >
                  Delete Game
                </button>
                </>
              )}
              <button type="submit" className="button-primary" disabled={submitting}>
                {submitting ? 'Saving...' : editingGame ? 'Update Game' : 'Create Game'}
              </button>
            </div>
          </form>
      </section>
      )}


      {activeTab === 'top-players' ? (
        <TopPlayersLeaderboard />
      ) : loading ? (
        <div className="empty-state">Loading games...</div>
      ) : currentItems.length === 0 ? (
        <div className="empty-state">
          {activeTab === 'games' && 'No games found. Try adjusting your filters or add a new game.'}
          {activeTab === 'challenges' && 'No challenges found. Try adjusting your filters.'}
        </div>
      ) : (
        <div
          className="game-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
            alignItems: 'start'
          }}
        >
          {currentItems.map((game) => {
            const availableDurations = getAvailableDurations(game);
            const currentPrice = getCurrentPrice(game);
            const selectedDuration = selectedDurations[game._id] || (game.units === 'per_over' ? '5overs' : 'default');
            
            return (
              <div className="game-card modern-card" key={game._id} style={{ position: 'relative', overflow: 'hidden', minHeight: isMobile ? '480px' : '520px' }}>
                {/* Edit button placed top-right below status pill */}
                {canEdit && !isPublicRoute && (
                  <div style={{ position: 'absolute', top: 44, right: 12, zIndex: 3 }}>
                    <button
                      type="button"
                      className="button-secondary"
                      onClick={() => handleEdit(game)}
                      disabled={submitting}
                      style={{ padding: '6px 10px', fontSize: '0.8rem' }}
                    >
                      Edit
                    </button>
                  </div>
                )}
                <div className="modern-card-media" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
                  <GameMediaCarousel
                    media={game.media}
                    placeholder={placeholderImage}
                    title={game.name}
                    game={game}
                    canViewBadges={canViewBadges}
                    showCategoryBadge={shouldShowCategoryBadge}
                  />
                </div>

                {/* Overlayed card body on top of media */}
                <div className="modern-card-body" style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2,
                  // stronger gradient so footer content is more legible over media
                  background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 55%, rgba(0,0,0,0.9) 100%)',
                  color: '#fff',
                  padding: '16px'
                }}>
                  {/* status pill handled elsewhere; Edit button moved into the backdrop row to avoid being hidden */}
                  <div style={{ display: 'block', padding: '12px', borderRadius: 12, background: 'rgba(0,0,0,0.28)', backdropFilter: 'blur(6px)', transition: 'background 0.18s ease' }}>
                    <div style={{ marginBottom: 8 }}>
                      <h3 className="modern-title" style={{ color: '#fff', margin: 0, textShadow: '0 2px 4px rgba(0,0,0,0.6)', fontSize: '1.05rem' }}>{game.name}</h3>
                      {game.description && (
                        <p className="modern-description" style={{ color: 'rgba(255,255,255,0.92)', margin: '6px 0 0 0', fontSize: '0.92rem', lineHeight: 1.2 }}>{game.description}</p>
                      )}
                    </div>

                    <div className="modern-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: 'transparent', padding: '6px 0', borderRadius: '8px' }}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div className="price-block">
                          <div className="price-label">Price</div>
                          <div className="price-value">{formatCurrency(currentPrice)}</div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {availableDurations && availableDurations.length > 0 && !game.challenge?.type && (
                          <select
                            value={selectedDuration}
                            onChange={(e) => handleDurationChange(game._id, e.target.value)}
                            style={{
                              padding: '6px 8px',
                              borderRadius: 8,
                              fontSize: '0.9rem',
                              minWidth: 100,
                              background: 'transparent',
                              border: '1px solid rgba(255,255,255,0.12)',
                              color: '#fff'
                            }}
                          >
                            {availableDurations.map((d) => (
                              <option key={d.value} value={d.value} style={{ background: 'var(--card-bg)', color: 'var(--text-primary)' }}>{d.label}</option>
                            ))}
                          </select>
                        )}

                        {game.challenge?.type && game.challenge?.price_per_participant ? (
                          participantSelecting[game._id] ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <button
                                type="button"
                                onClick={() => handleParticipantChange(game._id, -1)}
                                disabled={(selectedParticipants[game._id] || 1) <= 1}
                                style={{ padding: '8px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'transparent', color: '#fff', minWidth: 40 }}
                              >
                                −
                              </button>
                              <div style={{ minWidth: 36, textAlign: 'center', fontWeight: 700 }}>{selectedParticipants[game._id] || 1}</div>
                              <button
                                type="button"
                                onClick={() => handleParticipantChange(game._id, 1)}
                                disabled={(selectedParticipants[game._id] || 1) >= 100}
                                style={{ padding: '8px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'transparent', color: '#fff', minWidth: 40 }}
                              >
                                +
                              </button>
                              <button
                                type="button"
                                className="button-primary"
                                onClick={() => {
                                  addToCart(game);
                                  setParticipantSelecting((s) => ({ ...s, [game._id]: false }));
                                }}
                                style={{ marginLeft: 6 }}
                              >
                                Book Now
                              </button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button
                                type="button"
                                onClick={() => setParticipantSelecting((s) => ({ ...s, [game._id]: true }))}
                                style={{ padding: '6px 10px', borderRadius: 8, background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}
                              >
                                {selectedParticipants[game._id] || 1} participant{(selectedParticipants[game._id] || 1) > 1 ? 's' : ''} ▾
                              </button>
                              <button type="button" className="button-primary buy-now" onClick={() => addToCart(game)}>
                                Book Now
                              </button>
                            </div>
                          )
                        ) : (
                          <>
                            
                            <button type="button" className="button-primary buy-now" onClick={() => addToCart(game)}>
                              Book Now
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sticky Cart */}
          <aside
            className={`cart-sidebar${cartCollapsed ? ' collapsed' : ''}`}
          style={{
              width: cartCollapsed ? (isMobile ? '0px' : undefined) : undefined,
              position: isMobile ? 'fixed' : undefined,
              top: isMobile ? 0 : undefined,
              right: isMobile ? 0 : undefined,
              height: isMobile ? '100vh' : undefined,
              transform: isMobile && cartCollapsed ? 'translateX(100%)' : 'translateX(0)',
              transition: isMobile ? 'transform 0.25s ease' : undefined,
              // ensure modals (payment popup/options/preview/pos printer) appear above the cart on mobile
              zIndex: isMobile ? (paymentPopupOpen || paymentOptionsOpen || previewOpen || posPrinterOpen ? 9000 : 10002) : undefined,
            }}
            aria-hidden={cartCollapsed}
          >
        <div className="cart-header">
          <h3>Your Booking</h3>
          {(!isMobile && canEdit) && (
            <button className="button-secondary" onClick={() => setCartCollapsed((s) => !s)}>
              {cartCollapsed ? 'Open' : 'Collapse'}
            </button>
          )}
          {isMobile && (
            <button className="button-secondary" onClick={() => setCartCollapsed(true)} aria-label="Close cart">✕</button>
          )}
        </div>

        {(!cartCollapsed || isPublicRoute) && (
          <div className="cart-body">
                {cartItems.length === 0 ? (
                  <div className="empty-state">No items in cart.</div>
                ) : (
                  cartItems.map((it) => (
                    <div className="cart-item" key={`${it.id}-${Math.random()}`}>
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        {it.thumbUrl && (
                          it.mediaType === 'video' ? (
                            <video src={it.thumbUrl} width={64} height={48} style={{ objectFit: 'cover' }} />
                          ) : (
                            <img src={it.thumbUrl} width={64} height={48} style={{ objectFit: 'cover' }} alt="thumb" />
                          )
                        )}
                    <div>
                          <div className="cart-item-title">{it.name}</div>
                          <div className="cart-item-sub">{it.duration}</div>
                    </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="cart-item-price">{formatCurrency(it.price)}</div>
                        <button className="button-secondary" onClick={() => removeFromCart(it.id)}>✕</button>
                      </div>
                    </div>
                  ))
                )}

            <div className="cart-divider" />
            <div className="cart-summary">
              <div className="cart-summary-row"><span>Subtotal:</span><span>{formatCurrency(subtotal)}</span></div>
              <div className="cart-summary-row total"><span>Total:</span><span>{formatCurrency(total)}</span></div>
            </div>

            <button className="button-primary pay-now" onClick={openPaymentPopup}>Pay Now</button>
            {isMobile && (
              <button
                className="button-secondary"
                onClick={() => setCartCollapsed(true)}
                style={{ width: '100%', marginTop: 8 }}
                aria-label="Close cart"
              >
                Close
              </button>
            )}
          </div>
        )}
      </aside>

      {(!isMobile && cartCollapsed) && !isPublicRoute && (
        <button
          className="open-cart-button"
          onClick={() => setCartCollapsed(false)}
          aria-label="Open cart"
        >
          Open Cart
        </button>
      )}

      {/* Mobile backdrop when cart is open */}
      {isMobile && !cartCollapsed && (
        <div
          onClick={() => setCartCollapsed(true)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 10001
          }}
        />
      )}

      {/* Preview Modal */}
      {previewOpen && (
        <div
          className="media-preview-backdrop"
          onClick={closePreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            className="media-preview-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#111',
              padding: 12,
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
                  <button
                    type="button"
              className="media-preview-close"
              onClick={closePreview}
              style={{
                position: 'absolute',
                top: 12,
                right: 16,
                background: 'transparent',
                color: '#fff',
                border: 'none',
                fontSize: 24,
                cursor: 'pointer'
              }}
            >
              ×
                  </button>
            <div
              className="media-preview-body"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              {previewAssets[previewIndex]?.type === 'video' ? (
                <video
                  key={previewAssets[previewIndex].url}
                  src={previewAssets[previewIndex].url}
                  controls
                  autoPlay
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    borderRadius: 6
                  }}
                />
              ) : (
                <img
                  key={previewAssets[previewIndex].url}
                  src={previewAssets[previewIndex].url}
                  alt={`preview-${previewIndex}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                    borderRadius: 6
                  }}
                />
              )}
            </div>
            {previewAssets.length > 1 && (
              <div
                className="media-preview-controls"
                style={{
                  marginTop: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: '#fff'
                }}
              >
                <button
                  type="button"
                  onClick={prevPreview}
                  className="media-preview-nav"
                  style={{ padding: '6px 10px' }}
                >
                  ‹ Prev
                </button>
                <div className="media-preview-counter">{previewIndex + 1} / {previewAssets.length}</div>
                <button
                  type="button"
                  onClick={nextPreview}
                  className="media-preview-nav"
                  style={{ padding: '6px 10px' }}
                >
                  Next ›
                  </button>
                </div>
                )}
              </div>
        </div>
      )}

      {/* Payment Popup */}
      {paymentPopupOpen && (
        <div
          className="payment-popup-backdrop"
          onClick={closePaymentPopup}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 12000
          }}
        >
          <div
            className="payment-popup-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-lg)',
              padding: '20px',
              maxWidth: '1000px',
              width: '95vw',
              maxHeight: '90vh',
              // allow scrolling on small screens
              overflow: 'auto',
              boxShadow: 'var(--neon-glow)',
              display: 'flex',
              gap: '24px'
            }}
          >
            {/* Left Side - Payment Form */}
            <div style={{ flex: isMobile ? 1 : '0 0 35%', minWidth: '300px' }}>
              {/* Close button for mobile */}
              {isMobile && (
                <button
                  type="button"
                  onClick={closePaymentPopup}
                  aria-label="Close payment"
                  style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 13000,
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    fontSize: 22,
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              )}
              <div className="payment-popup-header" style={{ marginBottom: '24px' }}>
                <h2 style={{ 
                  margin: 0, 
                  color: 'var(--text-primary)', 
                  fontSize: '1.5rem',
                  fontWeight: '600'
                }}>
                  Complete Payment
                </h2>
                <p style={{ 
                  margin: '8px 0 0 0', 
                  color: 'var(--text-secondary)',
                  fontSize: '0.9rem'
                }}>
                  Total Amount: {formatCurrency(total)}
                </p>
              </div>

              <div className="payment-popup-body">
              <div className="payment-form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="userPhone" style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  color: 'var(--text-primary)',
                  fontWeight: '500'
                }}>
                  Phone Number * <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: '400' }}>(10 digits max)</span>
                </label>
                <input
                  id="userPhone"
                  type="tel"
                  value={userPhone}
                  onChange={handlePhoneChange}
                  onFocus={() => openVirtualKeyboard('phone')}
                  placeholder="Enter phone number"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'var(--input-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 'var(--border-radius-sm)',
                    color: 'var(--text-primary)',
                    fontSize: '1rem'
                  }}
                  disabled={searchingUser}
                />
                <div style={{ 
                  marginTop: '8px', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  fontSize: '0.8rem',
                  color: 'var(--text-secondary)'
                }}>
                  <div>
                    {searchingUser && (
                      <span style={{ color: 'var(--accent)' }}>
                        Searching for user...
                      </span>
                    )}
                  </div>
                  <div>
                    {userPhone.length}/10 digits
                  </div>
                </div>
              </div>

              {foundUser ? (
                <div className="user-found" style={{
                  background: 'rgba(0, 229, 255, 0.1)',
                  border: '1px solid rgba(0, 229, 255, 0.2)',
                  borderRadius: 'var(--border-radius-sm)',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ color: 'var(--accent)', fontWeight: '600', marginBottom: '4px' }}>
                    ✓ User Found
                  </div>
                  <div style={{ color: 'var(--text-primary)' }}>
                    {foundUser.name} ({foundUser.phone})
                  </div>
                </div>
              ) : userPhone.length >= 10 && !searchingUser ? (
                <div className="payment-form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="userName" style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    color: 'var(--text-primary)',
                    fontWeight: '500'
                  }}>
                    Name *
                  </label>
                  <input
                    id="userName"
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    onFocus={() => openVirtualKeyboard('name')}
                    placeholder="Enter customer name"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'var(--input-bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: 'var(--border-radius-sm)',
                      color: 'var(--text-primary)',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              ) : null}

              {userSearchError && (
                <div style={{
                  background: 'rgba(255, 99, 99, 0.1)',
                  border: '1px solid rgba(255, 99, 99, 0.2)',
                  borderRadius: 'var(--border-radius-sm)',
                  padding: '12px',
                  marginBottom: '20px',
                  color: '#ff6363',
                  fontSize: '0.9rem'
                }}>
                  {userSearchError}
                </div>
              )}

              <div className="payment-options" style={{ marginTop: '24px' }}>
                <h3 style={{ 
                  margin: '0 0 16px 0', 
                  color: 'var(--text-primary)', 
                  fontSize: '1.1rem',
                  fontWeight: '600'
                }}>
                  Choose Payment Method
                </h3>
                {proceedHint && (
                  <div style={{
                    marginBottom: '12px',
                    background: 'rgba(255, 193, 7, 0.08)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    borderRadius: 'var(--border-radius-sm)',
                    padding: '10px 12px',
                    color: '#ffc107',
                    fontSize: '0.85rem'
                  }}>
                    {proceedHint}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  <button
                    type="button"
                    className="payment-option-button"
                    onClick={handleProceedToPayment}
                    style={{
                      width: '100%',
                      padding: '16px 20px',
                      background: canProceedToPayment ? 'var(--accent)' : 'rgba(255, 255, 255, 0.05)',
                      color: canProceedToPayment ? '#0b1735' : 'var(--text-secondary)',
                      border: canProceedToPayment ? 'none' : '1px solid var(--card-border)',
                      borderRadius: 'var(--border-radius-sm)',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: canProceedToPayment ? 'pointer' : 'not-allowed',
                      transition: 'all var(--transition-fast)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: canProceedToPayment ? 1 : 0.7
                    }}
                    onMouseOver={(e) => {
                      if (!canProceedToPayment) return;
                      e.target.style.background = 'var(--accent-dark)';
                      e.target.style.transform = 'translateY(-2px)';
                    }}
                    onMouseOut={(e) => {
                      if (!canProceedToPayment) return;
                      e.target.style.background = 'var(--accent)';
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    💳 Proceed to Payment
                  </button>
                 </div>
               </div>
               {/* Show 'See receipt' small link to privileged users in the Complete Payment popup */}
               {currentAdmin && (currentAdmin.role === 1 || currentAdmin.role === '1' || currentAdmin.role === 2 || currentAdmin.role === '2') && (
                 <div style={{ marginTop: 8, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      // open POS printer above the payment popup by using a higher z-index
                      setPosPrinterOpen(true);
                    }}
                    style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                  >
                    See receipt
                  </button>
                 </div>
               )}
             </div>

              <div className="payment-popup-footer" style={{ 
                marginTop: '24px', 
                display: 'flex', 
                justifyContent: 'flex-end',
                gap: '12px'
              }}>
                <button
                  type="button"
                  onClick={closePaymentPopup}
                  style={{
                    padding: '10px 20px',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 'var(--border-radius-sm)',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Cancel
                  </button>
                </div>
              </div>

            {/* Right Side - Virtual Keyboard */}
            {virtualKeyboardOpen && (
              <div style={{ 
                flex: isMobile ? '0 0 450px' : 1, 
                background: 'var(--input-bg)', 
                borderRadius: 'var(--border-radius-lg)',
                padding: '20px',
                border: '1px solid var(--card-border)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Keyboard Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px',
                  paddingBottom: '8px',
                  borderBottom: '1px solid var(--card-border)'
                }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '0.9rem' }}>
                    {keyboardLayout === 'phone' ? '📱 Phone' : '⌨️ Text'}
                  </div>
                  {/* close button intentionally removed */}
                </div>
                

                {/* Keyboard Layout */}
                <div className="keyboard-layout" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  {keyboardLayout === 'phone' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '0 8px' }}>
                      {phoneKeys.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                          {row.map((key) => (
                            <button
                              key={key}
                              onClick={() => handleVirtualKeyPress(key)}
                              className="virtual-key"
                              style={{
                                flex: '1',
                                height: '50px',
                                background: key === 'backspace' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)',
                                border: `1px solid ${key === 'backspace' ? 'rgba(255, 99, 99, 0.3)' : 'var(--card-border)'}`,
                                borderRadius: 'var(--border-radius-sm)',
                                color: key === 'backspace' ? '#ff6363' : 'var(--text-primary)',
                                fontSize: key === 'backspace' ? '16px' : '20px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.1s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '0'
                              }}
                              onMouseDown={(e) => {
                                e.target.style.transform = 'scale(0.95)';
                                e.target.style.background = key === 'backspace' ? 'rgba(255, 99, 99, 0.3)' : 'var(--accent)';
                              }}
                              onMouseUp={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.background = key === 'backspace' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.background = key === 'backspace' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)';
                              }}
                            >
                              {key === 'backspace' ? '⌫' : key}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 4px' }}>
                      {textKeys.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ 
                          display: 'flex', 
                          gap: '4px', 
                          justifyContent: 'space-between',
                          marginLeft: rowIndex === 1 ? '8px' : rowIndex === 2 ? '16px' : '0px'
                        }}>
                          {row.map((key) => (
                            <button
                              key={key}
                              onClick={() => handleVirtualKeyPress(key)}
                              className="virtual-key"
                              style={{
                                flex: key === 'space' ? '3' : key === 'backspace' ? '1.5' : key === 'clear' ? '1.2' : '1',
                                height: '36px',
                                background: key === 'backspace' || key === 'clear' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)',
                                border: `1px solid ${key === 'backspace' || key === 'clear' ? 'rgba(255, 99, 99, 0.3)' : 'var(--card-border)'}`,
                                borderRadius: 'var(--border-radius-sm)',
                                color: key === 'backspace' || key === 'clear' ? '#ff6363' : 'var(--text-primary)',
                                fontSize: key === 'space' ? '11px' : key === 'backspace' || key === 'clear' ? '11px' : '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.1s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '0'
                              }}
                              onMouseDown={(e) => {
                                e.target.style.transform = 'scale(0.95)';
                                e.target.style.background = key === 'backspace' || key === 'clear' ? 'rgba(255, 99, 99, 0.3)' : 'var(--accent)';
                              }}
                              onMouseUp={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.background = key === 'backspace' || key === 'clear' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = 'scale(1)';
                                e.target.style.background = key === 'backspace' || key === 'clear' ? 'rgba(255, 99, 99, 0.2)' : 'var(--card-bg)';
                              }}
                            >
                              {key === 'backspace' ? '⌫' : key === 'clear' ? 'Clear' : key === 'space' ? 'Space' : key}
                            </button>
                          ))}
                        </div>
                      ))}
        </div>
      )}
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* POS Printer Modal */}
      <PosPrinter
        isOpen={posPrinterOpen}
        onClose={() => {
          setPosPrinterOpen(false);
          setSelectedPaymentMethod(null);
          setPosAutoPrint(false);
        }}
        cartItems={lastReceipt ? lastReceipt.cartItems : cartItems}
        userData={lastReceipt ? lastReceipt.userData : { name: foundUser ? foundUser.name : userName, phone: userPhone }}
        total={lastReceipt ? lastReceipt.total : total}
        subtotal={lastReceipt ? lastReceipt.subtotal : subtotal}
        tax={lastReceipt ? lastReceipt.tax : tax}
        transactionId={lastReceipt ? lastReceipt.transactionId : undefined}
        onPrintComplete={(printStatus) => {
          handlePrintComplete(printStatus);
          setPosAutoPrint(false);
        }}
        autoPrintNow={posAutoPrint}
        // ensure receipt modal appears above the payment popup when both are open
        backdropZIndex={paymentPopupOpen ? 13000 : 10001}
      />

      {/* Payment Options Modal */}
      {paymentOptionsOpen && (
        <div
          className="payment-options-backdrop"
          onClick={closePaymentOptions}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 12000
          }}
        >
          <div
            className="payment-options-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: 'var(--border-radius-lg)',
              padding: '32px',
              maxWidth: '500px',
              width: '90vw',
              textAlign: 'center',
              boxShadow: 'var(--neon-glow)'
            }}
          >
            {/* Timer */}
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'var(--accent)',
              color: '#0b1735',
              padding: '8px 12px',
              borderRadius: '20px',
              fontSize: '0.9rem',
              fontWeight: '600',
              minWidth: '60px'
            }}>
              {paymentTimer}s
            </div>

            {/* Header */}
            <h2 style={{
              margin: '0 0 24px 0',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              fontWeight: '600'
            }}>
              Complete Your Payment
            </h2>

            {/* QR Code */}
            <div style={{
              marginBottom: '24px',
              padding: '20px',
              background: '#ffffff',
              borderRadius: 'var(--border-radius-sm)',
              border: '1px solid var(--card-border)'
            }}>
              <div style={{
                textAlign: 'center',
                marginBottom: '12px',
                color: '#333',
                fontSize: '1rem',
                fontWeight: '600'
              }}>
                Scan QR Code
              </div>
              <img 
                src="/WhatsApp Image 2025-09-29 at 17.18.38.jpeg" 
                alt="Payment QR Code" 
                style={{
                  width: '100%',
                  maxWidth: '300px',
                  height: 'auto',
                  borderRadius: 'var(--border-radius-sm)'
                }}
              />
              {/* If the saved receipt is already paid, show subtle message and a Done button inside the QR card */}
              {lastReceipt && lastReceipt.payment_status === 'paid' && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
                    Already paid — you can dismiss this popup.
                  </div>
                  <button
                    type="button"
                    onClick={closePaymentOptions}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--card-border)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer' }}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>

            {/* OR Divider */}
            <div style={{
              textAlign: 'center',
              marginBottom: '16px',
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              fontWeight: '600',
              position: 'relative'
            }}>
              <span style={{
                background: 'var(--card-bg)',
                padding: '0 16px',
                position: 'relative',
                zIndex: 1
              }}>
                OR
              </span>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                background: 'var(--card-border)',
                zIndex: 0
              }}></div>
            </div>

            {/* Payment Options */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <button
                type="button"
                onClick={async () => {
                  const isPrivileged = currentAdmin && (currentAdmin.role === 1 || currentAdmin.role === '1');

                  // Privileged user: print and show receipt
                  if (isPrivileged) {
                    try {
                      if (lastReceipt && lastReceipt.transactionId) {
                        // update payment source to cash then print
                        await updateTransaction(lastReceipt.transactionId, { payment_source: 'cash' });
                        setLastReceipt(prev => prev ? { ...prev, payment_source: 'cash' } : prev);
                        setFeedback({ type: 'success', message: 'Marked as pay-at-counter (cash). Printing receipt.' });
                      } else {
                        // create transaction now and mark printed
                        await processPayment('Pay at Counter', 'now');
                      }

                      // open POS and auto-print
                      setSelectedPaymentMethod('Pay at Counter');
                      setPosAutoPrint(true);
                      setPosPrinterOpen(true);
                      closePaymentOptions();
                      return;
                    } catch (err) {
                      setFeedback({ type: 'error', message: err.message || 'Failed to process pay at counter' });
                      return;
                    }
                  }

                  // Non-privileged users: update existing transaction to cash if present, otherwise prompt
                  if (lastReceipt && lastReceipt.transactionId) {
                    try {
                      await updateTransaction(lastReceipt.transactionId, { payment_source: 'cash' });
                      setLastReceipt(prev => prev ? { ...prev, payment_source: 'cash' } : prev);
                    } catch (err) {
                    }
                    closePaymentOptions();
                    return;
                  }

                  setProceedHint('Please click Proceed to Payment first to create a transaction.');
                }}
                style={{
                  width: '100%',
                  padding: '16px 20px',
                  background: 'var(--accent)',
                  color: '#0b1735',
                  border: 'none',
                  borderRadius: 'var(--border-radius-sm)',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all var(--transition-fast)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  /* noop */
                }}
                onMouseOut={(e) => {
                  /* noop */
                }}
              >
                🏪 Pay at Counter
              </button>
            </div>

            {/* Close Button */}
            <button
              type="button"
              onClick={closePaymentOptions}
              style={{
                marginTop: '20px',
                padding: '10px 20px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--card-border)',
                borderRadius: 'var(--border-radius-sm)',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {/* Confirmation Modal */}
      {confirmOpen && (
        <div
          className="confirm-backdrop"
          onClick={() => setConfirmOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 11000
          }}
        >
          <div
            className="confirm-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--card-bg)',
              border: '1px solid var(--card-border)',
              borderRadius: '12px',
              padding: '20px',
              width: '420px',
              boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
              textAlign: 'center'
            }}
          >
            <div style={{ fontSize: 36 }}>⚠️</div>
            <h3 style={{ marginTop: 8, marginBottom: 8 }}>{confirmMessage}</h3>
            {confirmIsDestructive && (
              <div style={{ color: 'var(--text-secondary)', marginBottom: 16 }}>This action cannot be undone.</div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                type="button"
                className="button-secondary"
                onClick={() => setConfirmOpen(false)}
                style={{ padding: '10px 18px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className={confirmIsDestructive ? 'button-danger' : 'button-primary'}
                onClick={async () => {
                  setConfirmOpen(false);
                  if (confirmAction) await confirmAction();
                }}
                style={{ padding: '10px 18px' }}
              >
                {confirmIsDestructive ? 'Delete' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
